import { Controller } from '@hotwired/stimulus';
import {
  batchSetNodeKVPair,
  normalizeActions,
  updateDocumentUserState,
} from '../utils/case_experience_in_rails';

function refreshFeedbackSubmitButtons(application, scopeEl) {
  if (!scopeEl) return;
  scopeEl.querySelectorAll('[data-controller~="button-action"]').forEach((el) => {
    const c = application.getControllerForElementAndIdentifier(el, 'button-action');
    c?.updateButtonState?.();
  });
}

/** Feedback modal: textarea matches React — editable once every rating bar is unlocked (inactive !== 2). */
function syncFeedbackModalTextarea() {
  const root = document.getElementById('feedback-modal-content');
  if (!root) return;
  const textarea = root.querySelector('textarea');
  if (!textarea) return;

  const anyBarStillLocked = [...root.querySelectorAll('[data-controller~="rating-bar"]')].some(
    (el) => el.getAttribute('data-rating-bar-inactive-value') === '2',
  );
  textarea.disabled = anyBarStillLocked;
}

export default class extends Controller {
  static targets = ['star'];
  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    maxValue: Number,
    currentValue: Number,
    inactive: Number,
    onClick: Array,
  };

  connect() {
    this.focusedIndex = null;
    this._inactiveContainerKeydown = null;
    this.disableRating();
    this.updateStarDisplay();
    requestAnimationFrame(() => this.refreshSubmitButtonInFeedbackScope());
  }

  currentValueValueChanged() {
    this.updateStarDisplay();
  }

  inactiveValueChanged() {
    this.disableRating();
    this.updateStarDisplay();
  }

  disconnect() {
    if (this._inactiveContainerKeydown) {
      this.element.removeEventListener('keydown', this._inactiveContainerKeydown);
      this._inactiveContainerKeydown = null;
    }
  }

  focusStarByRatingIndex(ratingIndex) {
    this.element.querySelector(`[data-rating-index="${ratingIndex}"]`)?.focus();
  }

  refreshSubmitButtonInFeedbackScope() {
    const scope =
      this.element.closest('#feedback-modal-content') ||
      this.element.closest('.doc-section.feedback_card');
    if (!scope) return;
    refreshFeedbackSubmitButtons(this.application, scope);
  }

  disableRating() {
    const isInactive = this.inactiveValue === 2;
    this.element.classList.toggle('inactive', isInactive);

    if (this._inactiveContainerKeydown) {
      this.element.removeEventListener('keydown', this._inactiveContainerKeydown);
      this._inactiveContainerKeydown = null;
    }

    if (isInactive) {
      // One tab stop for the whole locked bar so focus traps and Tab order can reach this
      // section (stars use tabindex -1 until the bar is unlocked).
      this.element.tabIndex = 0;
      this.element.setAttribute('aria-disabled', 'true');
      this._inactiveContainerKeydown = (e) => {
        if (e.key === 'Tab') return;
        if (
          e.key === 'Enter' ||
          e.key === ' ' ||
          e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight' ||
          e.key === 'ArrowUp' ||
          e.key === 'ArrowDown'
        ) {
          e.preventDefault();
        }
      };
      this.element.addEventListener('keydown', this._inactiveContainerKeydown);
      this.starTargets.forEach((star) => {
        star.tabIndex = -1;
        star.setAttribute('aria-disabled', 'true');
        star.classList.add('inactive');
      });
    } else {
      const hadContainerFocus = document.activeElement === this.element;
      this.element.removeAttribute('tabindex');
      this.element.removeAttribute('aria-disabled');
      this.starTargets.forEach((star) => {
        star.tabIndex = 0;
        star.setAttribute('aria-disabled', 'false');
        star.classList.remove('inactive');
      });
      if (hadContainerFocus) {
        requestAnimationFrame(() => this.focusStarByRatingIndex(0));
      }
    }

    if (document.getElementById('feedback-modal-content')?.contains(this.element)) {
      syncFeedbackModalTextarea();
    }
    if (
      document.getElementById('feedback-modal-content')?.contains(this.element) ||
      this.element.closest('.doc-section.feedback_card')
    ) {
      this.refreshSubmitButtonInFeedbackScope();
    }
  }

  selectRating(event) {
    if (this.inactiveValue === 2) return;
    const { ratingIndex } = event.currentTarget.dataset || {};
    if (ratingIndex === undefined) return;
    const index = Number(ratingIndex);
    if (Number.isNaN(index)) return;
    void this.updateRating(index + 1);
  }

  handleKeyDown(event) {
    if (this.inactiveValue === 2) return;
    const currentStar = event.currentTarget;
    const { ratingIndex } = currentStar.dataset || {};
    if (ratingIndex === undefined) return;
    const index = Number(ratingIndex);
    if (Number.isNaN(index)) return;
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectRating(event);
        break;
      case 'ArrowLeft':
      case 'ArrowDown': {
        event.preventDefault();
        const targetIndex = index > 0 ? index - 1 : this.maxValueValue - 1;
        void this.updateRating(targetIndex + 1);
        this.focusStarByRatingIndex(targetIndex);
        break;
      }
      case 'ArrowRight':
      case 'ArrowUp': {
        event.preventDefault();
        const targetIndex = index < this.maxValueValue - 1 ? index + 1 : 0;
        void this.updateRating(targetIndex + 1);
        this.focusStarByRatingIndex(targetIndex);
        break;
      }
    }
  }

  async updateRating(newValue) {
    this.currentValueValue = newValue;
    this.updateStarDisplay();

    const body = {
      document_user_state: {
        state: {
          [this.nodeUidValue]: {
            value: {
              value: newValue,
              updatedAt: Date.now(),
            },
          },
        },
      },
    };

    try {
      await updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body,
      });
      await this.handleOnClickActions();
      this.refreshSubmitButtonInFeedbackScope();
    } catch (error) {
      // Only log if not aborted by navigation
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('Error updating rating:', error);
      }
    }
  }

  updateStarDisplay() {
    const pressedIndex =
      this.currentValueValue > 0 ? this.currentValueValue - 1 : null;
    this.starTargets.forEach((star) => {
      const ratingIndex = Number(star.dataset.ratingIndex);
      if (Number.isNaN(ratingIndex)) return;
      const isActive =
        this.currentValueValue > 0 && ratingIndex < this.currentValueValue;
      star.classList.toggle('doc-rating-bar-star-active', isActive);
      const isPressed = pressedIndex !== null && ratingIndex === pressedIndex;
      star.setAttribute('aria-pressed', isPressed ? 'true' : 'false');
    });
  }

  async handleOnClickActions() {
    const normalizedActions = normalizeActions(this.onClickValue);

    const nodeKvPairActions = normalizedActions.filter((a) => a.action === 'setNodeKVPair');

    if (nodeKvPairActions.length > 0) {
      await batchSetNodeKVPair(nodeKvPairActions, this.documentIdValue, this.userIdValue);
      nodeKvPairActions.forEach(({ arguments: { uid, key, value } }) => {
        const node = document.querySelector(`[data-uid="${uid}"]`);
        if (!node) return;
        if (key === 'inactive') {
          // Only set if controller is present
          const ratingController = this.application.getControllerForElementAndIdentifier(
            node,
            'rating-bar',
          );
          if (ratingController) {
            node.setAttribute('data-rating-bar-inactive-value', value);
          }
          const buttonController = this.application.getControllerForElementAndIdentifier(
            node,
            'button-action',
          );
          if (buttonController) {
            node.setAttribute('data-button-action-inactive-value', value);
          }
        }
        if (key === 'invisible') {
          const buttonController = this.application.getControllerForElementAndIdentifier(
            node,
            'button-action',
          );
          if (buttonController) {
            node.setAttribute('data-button-action-invisible-value', value ? 'true' : 'false');
          }
        }
      });
    }

    if (document.getElementById('feedback-modal-content')?.contains(this.element)) {
      syncFeedbackModalTextarea();
    }
    if (
      document.getElementById('feedback-modal-content')?.contains(this.element) ||
      this.element.closest('.doc-section.feedback_card')
    ) {
      this.refreshSubmitButtonInFeedbackScope();
    }
  }
}
